import React from "react";
import _ from "lodash";
import styles from "./FHIRPatient.module.scss";
import PatientName from "./PatientName";
import Address from "./Address";
import Telecom from "./Telecom";
import Identifier from "./Identifier";
import CoverageIMDM from "./CoverageIMDM";
import Gender from "./Gender";
import BirthDate from "./BirthDate";
import { patientIdentifiers, patientCoverages } from "./Utils";
import {
  ExtensionURLs,
  getValueTypeByAttribute,
} from "./../../routes/NewSearchRecords/SearchRecordTable/GoldenRecordExclusion/Utils";

/**
 * Used to determine and display excluded values in the aggregate details section as it relates to the concept of
 * Golden Record Exclusion. A value is considered excluded if it is core to the attribute and has been overridden by
 * a different value. An "inactive" override type is not considered excluded. This component will display the excluded
 * values for name, address, telecom, identifier, birthDate, gender, and multipleBirthBoolean.
 *
 * @param {Object} data - Bundle of primary records.
 * @returns {JSX.Element} - The excluded values display component.
 */
const ExcludedValues = ({ primaryRecords, isGoldenRecordExclusionEnabled }) => {
  const excludedNames = new Array();
  const excludedAddresses = new Array();
  const excludedTelecoms = new Array();
  const excludedIdentifiers = new Array();
  const excludedBirthDates = new Array();
  const excludedGenders = new Array();
  const excludedMultipleBirthBooleans = new Array();
  const emptySection = <div>Empty</div>;

  // If Golden Record Exclusion is not enabled, return an empty section.
  if (!isGoldenRecordExclusionEnabled) {
    return emptySection;
  }

  /**
   * Helper function to check if all excluded values arrays are empty.
   *
   * @param {Array} excludedValues - An array of excluded values arrays.
   * @returns {Boolean} - True if all arrays are empty, false otherwise.
   */
  const isEmptyExcludedValues = (excludedValues) => {
    return excludedValues.every((array) => _.isEmpty(array));
  };

  /**
   * Helper function to get the values of the name object excluding the "use" and "period" elements.
   *
   * @param {Object} obj - The name object.
   * @returns {Object} - The values of the name object.
   */
  const nameValue = (obj) => {
    const values = new Object();
    if (obj.hasOwnProperty("family")) {
      values.family = obj.family.toUpperCase();
    }
    if (obj.hasOwnProperty("given")) {
      values.given = obj.given.map((n) => n.toUpperCase());
    }
    if (obj.hasOwnProperty("prefix")) {
      values.prefix = obj.prefix.toUpperCase();
    }
    if (obj.hasOwnProperty("suffix")) {
      values.suffix = obj.suffix.toUpperCase();
    }
    return values;
  };

  /**
   * Helper function to get the values of the name object excluding the "use" and "period" elements.
   *
   * @param {Object} obj - The address object.
   * @returns {Object} - The values of the address object.
   */
  const addressValue = (obj) => {
    const values = new Object();
    if (obj.hasOwnProperty("line")) {
      values.line = obj.line.map((l) => l.toUpperCase());
    }
    if (obj.hasOwnProperty("city")) {
      values.city = obj.city.toUpperCase();
    }
    if (obj.hasOwnProperty("district")) {
      values.district = obj.district.toUpperCase();
    }
    if (obj.hasOwnProperty("state")) {
      values.state = obj.state.toUpperCase();
    }
    if (obj.hasOwnProperty("country")) {
      values.country = obj.country.toUpperCase();
    }
    if (obj.hasOwnProperty("postalCode")) {
      values.postalCode = obj.postalCode.toUpperCase();
    }
    return values;
  };

  /**
   * Helper function to get the values of the name object excluding the "use" and "period" elements.
   *
   * @param {Object} obj - The telecom object.
   * @returns {Object} - The values of the telecom object.
   */
  const telecomValue = (obj) => {
    const values = new Object();
    if (obj.hasOwnProperty("system")) {
      values.system = obj.system.toUpperCase();
    }
    if (obj.hasOwnProperty("value")) {
      values.value = obj.value.toUpperCase();
    }
    return values;
  };

  /**
   * Helper function to get the values of the name object excluding the "use" and "period" elements.
   *
   * @param {Object} obj - The identifier object.
   * @returns {Object} - The values of the identifier object.
   */
  const identifierValue = (obj) => {
    const values = _.cloneDeep(obj);
    if (obj.hasOwnProperty("period")) {
      delete values.period;
    }
    if (obj.hasOwnProperty("id")) {
      delete values.id;
    }
    if (obj.hasOwnProperty("use")) {
      delete values.use;
    }
    if (obj.hasOwnProperty("extension")) {
      delete values.extension;
    }
    return values;
  };

  /**
   * Helper function to get the values of an attribute object using the attribute value functions above.
   *
   * @param {String} attribute - The attribute to get the values for.
   * @param {Object} obj - The object to get the values from.
   * @returns {Object} - The values of the attribute object.
   */
  const getValues = (attribute, obj) => {
    switch (attribute) {
      case "name":
        return nameValue(obj);
      case "address":
        return addressValue(obj);
      case "telecom":
        return telecomValue(obj);
      case "identifier":
        return identifierValue(obj);
    }
  };

  /**
   * Helper function to determine if oldValues should be excluded based on newValues.
   *
   * @param {Object} newVals - The new values.
   * @param {Object} oldVals - The old values.
   * @returns {Boolean} - True if newVals and oldVals are not equal.
   */
  const isValueExcluded = (newVals, oldVals) => {
    return !_.isEqual(newVals, oldVals);
  };

  /**
   * Helper function to add excluded values to arrays for top level extension items (birthDate, gender and multipleBirth).
   *
   * @param {Object} record - A record in the primary records bundle.
   */
  const addExtensionOverrideValues = (record) => {
    if (record.extension) {
      record.extension.forEach((ext) => {
        if (
          ext.url === ExtensionURLs.birthDateOverride &&
          isValueExcluded(record.birthDate, ext.valueDate)
        ) {
          excludedBirthDates.push(ext.valueDate);
        }
        if (
          ext.url === ExtensionURLs.genderOverride &&
          isValueExcluded(record.gender, ext.valueString)
        ) {
          excludedGenders.push(ext.valueString);
        }
        if (
          ext.url === ExtensionURLs.multipleBirthBooleanOverride &&
          isValueExcluded(record.multipleBirthBoolean, ext.valueBoolean)
        ) {
          switch (ext.valueBoolean) {
            case true:
              excludedMultipleBirthBooleans.push("Yes");
            case false:
              excludedMultipleBirthBooleans.push("No");
          }
        }
      });
    }
  };

  /**
   * Helper function to add excluded values to arrays for attribute items (name, address, telecom, and identifier).
   *
   * @param {String} attribute - The attribute to add values for.
   * @param {Object} object - The object to get the values from.
   * @param {Array} exclusionArray - The array to add the values to.
   */
  const addOverrideValues = (attribute, object, exclusionArray) => {
    const attributeValueType = getValueTypeByAttribute(attribute);

    if (object) {
      object.forEach((item) => {
        if (item.extension) {
          item.extension.forEach((ext) => {
            if (
              ext.url === ExtensionURLs.genericOverride &&
              ext[attributeValueType]
            ) {
              const newValues = getValues(attribute, item);
              const oldValues = getValues(attribute, ext[attributeValueType]);

              if (isValueExcluded(newValues, oldValues)) {
                exclusionArray.push(oldValues);
              }
            }
          });
        }
      });
    }
  };

  // Looping through the bundle of primary records to find values that have been overridden and adding them to their
  // respective arrays.
  primaryRecords.forEach((record) => {
    // Top level extension overrides will be compared first (birthDate, gender and multipleBirth).
    addExtensionOverrideValues(record);
    // Then other attributes will be compared (name, address, telecom, and identifier).
    addOverrideValues("name", record.name, excludedNames);
    addOverrideValues("address", record.address, excludedAddresses);
    addOverrideValues("telecom", record.telecom, excludedTelecoms);
    addOverrideValues("identifier", record.identifier, excludedIdentifiers);
  });

  // Using lodash uniqWith and isEqual to remove duplicates to get only unique values for each attribute array.
  const uniqueNameValues = _.uniqWith(excludedNames, _.isEqual);
  const uniqueAddressValues = _.uniqWith(excludedAddresses, _.isEqual);
  const uniqueTelecomValues = _.uniqWith(excludedTelecoms, _.isEqual).sort(
    (a, b) => a.system.localeCompare(b.system),
  );
  const uniqueIdentifierValues = _.uniqWith(excludedIdentifiers, _.isEqual);

  // Creating a Set() to get unique values and converting to comma separated as this is what FHIRDetails.BirthDate
  // and FHIRDetails.Gender currently accepts for multiple values.
  const uniqueBirthDateValues = Array.from(new Set(excludedBirthDates)).join(
    ",",
  );
  const uniqueGenderValues = Array.from(new Set(excludedGenders)).join(",");
  const uniqueMultipleBirthBooleanValues = _.uniqWith(
    excludedMultipleBirthBooleans,
    _.isEqual,
  );

  // Creating the JSX elements for each attribute to display the excluded values.
  const name = (
    <PatientName
      className={styles.name}
      data={uniqueNameValues}
      isAggregateInfo={true}
    />
  );

  const address = (
    <Address
      className={styles.address}
      data={uniqueAddressValues}
      isAggregateInfo={true}
    />
  );

  const telecom = (
    <Telecom
      className={styles.telecom}
      data={uniqueTelecomValues}
      isAggregateInfo={true}
    />
  );

  const identifiers = (
    <Identifier
      className={styles.name}
      data={patientIdentifiers(uniqueIdentifierValues)}
      isAggregateInfo={true}
      isExcludedValues={true}
    />
  );

  const coverageIMDM = (
    <CoverageIMDM
      className={styles.coverageIMDM}
      data={patientCoverages(uniqueIdentifierValues)}
      isAggregateInfo={true}
    />
  );

  const gender = <Gender data={uniqueGenderValues} isAggregateInfo={true} />;

  const dob = <BirthDate data={uniqueBirthDateValues} isAggregateInfo={true} />;

  // Used for checking if all arrays are empty and "Empty" will be displayed if there are no excluded values.
  const excludedValues = [
    excludedNames,
    excludedAddresses,
    excludedTelecoms,
    excludedIdentifiers,
    excludedBirthDates,
    excludedGenders,
  ];

  const excludedValuesSection = (
    <div>
      {!_.isEmpty(excludedNames) && (
        <div>
          <div className={styles.heading}>Name</div>
          <div className={styles.info}>{name}</div>
        </div>
      )}
      {!_.isEmpty(excludedAddresses) && (
        <div>
          <div className={styles.heading}>Address</div>
          <div className={styles.info}>{address}</div>
        </div>
      )}
      {(!_.isEmpty(excludedBirthDates) ||
        !_.isEmpty(excludedGenders) ||
        !_.isEmpty(excludedMultipleBirthBooleans)) && (
        <div>
          <div className={styles.heading}>Basic Demographics</div>
          {!_.isEmpty(excludedGenders) && (
            <div className={styles.info}>{gender}</div>
          )}
          {!_.isEmpty(excludedBirthDates) && (
            <div className={styles.info}>{dob}</div>
          )}
          {!_.isEmpty(excludedMultipleBirthBooleans) && (
            <div className={styles.info}>
              <h4>Multiple Birth</h4>
              {uniqueMultipleBirthBooleanValues.map((mb) => (
                <div>{mb}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {!_.isEmpty(excludedTelecoms) && (
        <div>
          <div className={styles.heading}>Telecom</div>
          <div className={styles.info}>{telecom}</div>
        </div>
      )}
      {!_.isEmpty(patientIdentifiers(excludedIdentifiers)) && (
        <div>
          <div className={styles.heading}>Identifier</div>
          <div className={styles.info}>{identifiers}</div>
        </div>
      )}
      {!_.isEmpty(patientCoverages(excludedIdentifiers)) && (
        <div>
          <div className={styles.heading}>Coverage</div>
          <div className={styles.info}>{coverageIMDM}</div>
        </div>
      )}
    </div>
  );

  switch (isEmptyExcludedValues(excludedValues)) {
    case true:
      return emptySection;
    default:
      return excludedValuesSection;
  }
};

export default ExcludedValues;
